import { ReactNode, useEffect, useMemo, useState } from "react"

import * as Portal from "@radix-ui/react-portal"
import {
  IconAlertOctagonFilled,
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconInfoCircleFilled,
  IconLoader,
  IconX,
} from "@tabler/icons-react"
import toast, {
  Renderable,
  Toast as ToastObject,
  ToastOptions,
  ToastType,
  Toaster,
  ValueOrFunction,
  resolveValue,
} from "react-hot-toast"

import { cn } from "@/lib/utils"

import { Button } from "./Button"

type ToastTypes = "success" | "error" | "message" | "caution" | "loading"

export interface ToastProps {
  icon?: ReactNode
  children: ReactNode
  type?: ToastTypes
  className?: string
  bodyClassName?: string
  showClose?: boolean
  onClose?: () => void
  hideIcon?: boolean
  action?: {
    label: string
    onClick: () => void
  }
}

export function Toast({
  icon,
  children,
  type = "message",
  className,
  bodyClassName,
  onClose,
  hideIcon = false,
  action,
  showClose = true,
}: ToastProps) {
  const colorClasses = {
    success: /*tw*/ "border-success-500 bg-success-100 text-success-900",
    error: /*tw*/ "border-error-400 bg-error-100 text-error-900",
    message:
      /*tw*/ "border-brand-primary-300 bg-brand-primary-100 text-brand-primary-600",
    caution: /*tw*/ "border-accent-3-500 bg-accent-3-100 text-accent-3-900",
    loading: /*tw*/ "border-accent-1-500 bg-accent-1-200 text-accent-1-700",
  }[type]

  const typeIcon = {
    success: IconCircleCheckFilled,
    error: IconAlertOctagonFilled,
    message: IconInfoCircleFilled,
    caution: IconAlertTriangleFilled,
    loading: IconLoader,
  }[type]

  const TypeIcon = typeIcon

  return (
    <div
      className={cn(
        "flex gap-2 rounded-md border border-solid p-4",
        colorClasses,
        className,
      )}
    >
      {!hideIcon
        ? icon || (
            <TypeIcon
              size={20}
              className={cn("shrink-0", {
                "animate-spin": type === "loading",
              })}
            />
          )
        : null}

      <span className={cn("flex grow flex-col gap-2 text-body1", bodyClassName)}>
        {children}
        {action && (
          <div className="flex">
            <Button
              variant="neutral"
              size="small"
              onClick={action?.onClick}
              className="whitespace-nowrap"
            >
              {action?.label}
            </Button>
          </div>
        )}
      </span>

      {showClose ? (
        <IconX
          size={20}
          role="button"
          onClick={onClose}
          className="shrink-0 text-grey-700"
        />
      ) : null}
    </div>
  )
}

// get Toast props based on the `t` object from `react-hot-toast`
function getToastProps(t: CustomToastObject) {
  return {
    icon: t.icon,
    onClose: () => toast.dismiss(t.id),
    className: cn("transition-opacity", {
      "animate-leave": !t.visible,
      "animate-enter": t.visible,
    }),
    action: t.action
      ? {
          label: t.action.label,
          onClick: () => t.action?.onClick(t),
        }
      : undefined,
    showClose: t.showClose,
  }
}

export function ToastContainer() {
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => setHasMounted(true), [])
  if (!hasMounted) return null

  const typeMap: Record<ToastType, ToastProps["type"]> = {
    blank: "message",
    custom: "message",
    error: "error",
    loading: "loading",
    success: "success",
  }

  return (
    <Portal.Root>
      <Toaster position="bottom-right">
        {(t: CustomToastObject) => (
          <Toast type={typeMap[t.type]} {...getToastProps(t)}>
            {resolveValue(t.message, t)}
          </Toast>
        )}
      </Toaster>
    </Portal.Root>
  )
}

type ToastExtraOptions = {
  action?: {
    label: string
    onClick: (t: ToastObject) => void
  }
  showClose?: ToastProps["showClose"]
}
type CustomToastObject = ToastObject & ToastExtraOptions
type CustomToastOptions = ToastOptions & ToastExtraOptions

type ToastHandler = (
  message: ValueOrFunction<Renderable, CustomToastObject>,
  options?: CustomToastOptions,
) => string

const createToastHandler: (type: ToastTypes) => ToastHandler =
  (type) => (message, options) => {
    return toast.custom(
      (t: CustomToastObject) => (
        <Toast type={type} {...getToastProps(t)}>
          {resolveValue(message, t)}
        </Toast>
      ),
      {
        duration: 7000,
        ...options,
      },
    )
  }

/**
 * custom hook to expose custom handlers for triggering our custom toast notifications with extra options and other toast types
 * use this instead of calling the `toast` function from `react-hot-toast` directly
 */
export const useToast = () => {
  const handlers = useMemo(() => {
    const errorToastHandler = createToastHandler("error")
    const successToastHandler = createToastHandler("success")

    async function promise<T>(
      promise: Promise<T>,
      msgs: {
        loading: Renderable
        success: ValueOrFunction<Renderable, T>
        error: ValueOrFunction<Renderable, any>
      },
      opts?: ToastOptions & {
        [key in ToastType]?: CustomToastOptions
      },
      responseValidator?: (response: T) => string | null,
    ) {
      const loadingId = toast.loading(msgs.loading, { ...opts, ...opts?.loading })
      const defaultValidator = () => null
      const validate = responseValidator ?? defaultValidator

      promise
        .then((data) => {
          toast.remove(loadingId)

          const errorMsg = validate(data)

          if (errorMsg) {
            errorToastHandler(
              typeof msgs.error === "function" ? msgs.error(errorMsg) : msgs.error,
              { ...opts, ...opts?.error },
            )
          } else {
            successToastHandler(
              typeof msgs.success === "function" ? msgs.success(data) : msgs.success,
              { ...opts, ...opts?.success },
            )
          }
        })
        .catch((reason: unknown) => {
          toast.remove(loadingId)
          errorToastHandler(
            typeof msgs.error === "function" ? msgs.error(reason) : msgs.error,
            { ...opts, ...opts?.error },
          )
        })

      return promise
    }

    // Minimal toast config for admin.
    function lazy<T>(
      p: Promise<T>,
      responseValidator?: (response: T) => string | null,
    ) {
      return promise(
        p,
        {
          loading: "submitting…",
          success: "success!",
          error: (err) => `Error: ${err}`,
        },
        {},
        responseValidator,
      )
    }

    return {
      success: successToastHandler,
      error: errorToastHandler,
      message: createToastHandler("message"),
      caution: createToastHandler("caution"),
      loading: toast.loading as ToastHandler,
      promise,
      lazy,
      dismiss: toast.remove,
    }
  }, [])

  return handlers
}
